<template>
  <c-container fluid class="d-flex content-center min-vh-100 login-container c-app c-dark-theme">
    <logo-long-boss />
    <c-row>
      <c-col>
        <c-card-group>
          <c-card class="p-4 confirm-account-form">
            <c-card-body>
              <c-form>
                <template v-if="user">
                  <template v-if="!activationSuccess">
                    <c-row>
                      <c-col sm="14" md="6">
                        <h2 class="mb-4">Hey {{ user.firstName }} {{ user.lastName }}</h2>
                        <c-img width="100%" src="https://s3-ap-southeast-2.amazonaws.com/codebrewery-digital-assets/mainpress/app/video-content/activate.gif"/>
                        <p class="lead mt-4">We have just built your KoalaBoss dashboard.</p>
                      </c-col>
                      <c-col sm="14" md="6">
                        <div id="set-password">
                          <h2 class="text-left mb-4">Set your password, and jump on Falkor.</h2>
                          <p class="text-left">Please enter a new password which contains -</p>
                          <ul class="text-left mb-4">
                            <li>At least 8 characters</li>
                            <li>At least one number</li>
                            <li>An uppercase letter</li>
                            <li>A lowercase letter</li>
                          </ul>
                          <c-alert v-if="error" show v-html="error" color="danger" />
                          <c-form>
                            <CInput
                                autofocus
                                autocomplete="off"
                                v-model="password"
                                type="password"
                                placeholder="Password"
                                :isValid="checkIfValid('password')"
                                invalidFeedback="Please enter new password"
                            />
                            <CInput
                                autocomplete="off"
                                v-model="confirmPassword"
                                type="password"
                                placeholder="Confirm password"
                                :isValid="checkIfValid('confirmPassword')"
                                invalidFeedback="Passwords do not match"
                            />
                          </c-form>
                          <c-row class="my-4">
                            <CCol sm="12">
                              <CSwitch
                                  id="acceptTerms"
                                  class="float-left pt-1"
                                  color="success"
                                  :checked="false"
                                  shape="pill"
                                  @update:checked="handleAcceptTerms"
                              />
                              <p class="text-left pl-5 pt-0">By activating your account you accept the KoalaBoss <a href="https://koalaboss.com/terms-of-use/" class="color-kb-green" target="_blank">terms of use</a>.</p>
                            </CCol>
                          </c-row>
                          <c-button @click="activateAccount" color="primary w-100" :disabled="!acceptTerms">
                            <CSpinner v-if="isActivating" color="success" size="sm"/>
                            <span v-else>Activate account</span>
                          </c-button>
                        </div>
                      </c-col>
                    </c-row>
                  </template>
                  <template v-else>
                    <h2 class="mb-4">All set!</h2>
                    <p class="lead mb-4">Jumping in to your KoalaBoss dashboard now...</p>
                    <c-spinner color="success" />
                  </template>
                </template>
                <template v-else>
                  <c-alert v-if="error" show color="danger" v-html="error" />
                  <template v-else>
                    <h2 class="mb-4">Checking account...</h2>
                    <c-spinner color="success" />
                  </template>
                </template>
              </c-form>
            </c-card-body>
          </c-card>
        </c-card-group>
      </c-col>
    </c-row>
  </c-container>
</template>

<script>
import _ from 'lodash';
import { validationMixin } from 'vuelidate';
import {
  required, minLength, sameAs, helpers,
} from 'vuelidate/lib/validators';
import { buildEndpoint } from '@/libs/buildEndPoint';
import LogoLongBoss from '@/views/components/images/long-boss';

export default {
  components: {
    LogoLongBoss,
  },
  mixins: [validationMixin],
  data() {
    return {
      error: null,
      isActivating: false,
      user: null,
      fullName: '',
      activationSuccess: false,
      isLoggingIn: false,
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    };
  },
  computed: {
    isValid() {
      return !this.$v.$invalid;
    },
  },
  validations: {
    password: {
      required,
      minLength: minLength(8),
      strongPass: helpers.regex('strongPass', /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/),
    },
    confirmPassword: {
      required,
      sameAsPassword: sameAs('password'),
    },
  },
  mounted() {
    this.checkConfirmationKey();
  },
  methods: {
    handleAcceptTerms(e) {
      this.acceptTerms = e;
    },
    checkIfValid(fieldName) {
      const field = this.$v[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === '');
    },
    checkConfirmationKey() {
      this.error = null;
      this.$http
        .get(buildEndpoint('account/confirmation/') + this.$route.params.confirmationKey)
        .then((response) => {
          if (response.data.success) {
            this.user = response.data.data;
          } else {
            this.error = 'Sorry! Your KoalaBoss invitation has expired. Please contact your administrator to get a new one.';
          }
        })
        .catch((error) => {
          this.error = error;
        });
    },
    activateAccount() {
      this.$v.$touch();
      if (!this.isValid) {
        return;
      }

      this.isActivating = true;
      this.error = null;
      const data = {
        password: this.password,
      };
      this.$http
        .post(buildEndpoint('account/confirmation/save/') + this.$route.params.confirmationKey, data)
        .then((response) => {
          this.isActivating = false;
          if (response.data.success) {
            this.activationSuccess = true;
            setTimeout(this.autoLogin, 2000);
          } else {
            this.error = response.data.message;
          }
        })
        .catch((error) => {
          this.error = error;
        });
    },
    autoLogin() {
      this.isLoggingIn = true;

      // Get rid of surrounding spaces
      this.username = _.trim(this.user.username);
      this.password = _.trim(this.password);

      this.$auth.login({
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          username: this.username,
          password: this.password,
        },
        redirect: { name: 'dashboard', query: { refresh: '1' } },
        success(res) {
          // Do nothing
        },
        error(err) {
          this.isLoggingIn = false;
          if (err.response) {
            // The request was made, but the server responded with a status code
            // that falls out of the range of 2xx
            this.error = err.response.data.message;
          } else {
            // Something happened in setting up the request that triggered an Error
            this.error = 'There was an error logging into KoalaBoss, please try again.';
          }
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.terms {
  height: 200px;
  overflow-y: scroll;
  border: 1px solid silver;
  padding: 5px;
}
</style>
